import { create } from 'zustand';

export const useAppStore = create((set, get) => ({
  // State
  theme: 'light',
  sidebarOpen: false,
  notifications: [],
  bidOptions: [],
  shortlistedBids: [],
  shortlistAircraft: [],
  bids: [],
  bidOptionsLoading: true,
  bidsLoading: true,
  quotes: [],
  quotesLoading: true,
  selectedBid: null,
  selectedTripGroup: null, // { tripNumber: string, bids: array }
  selectedAircraft: null,
  selectedBidOptions: [], // Array of selected bid options for checkout
  
  // Actions
  setTheme: (theme) => set({ theme }),
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  
  addNotification: (notification) => 
    set((state) => ({ 
      notifications: [...state.notifications, { 
        id: Date.now(), 
        ...notification 
      }] 
    })),
    
  removeNotification: (id) => 
    set((state) => ({ 
      notifications: state.notifications.filter(n => n.id !== id) 
    })),
    
  clearNotifications: () => set({ notifications: [] }),
  
  // Bid Options Actions
  setBidOptions: (options) => set({ bidOptions: options, bidOptionsLoading: false }),
  
  setBidOptionsLoading: (loading) => set({ bidOptionsLoading: loading }),
  
  updateBidOption: (bidOptionId, updates) =>
    set((state) => ({
      bidOptions: state.bidOptions.map(option =>
        option.bid_option_id === bidOptionId ? { ...option, ...updates } : option
      )
    })),

  // Bids Actions
  setBids: (bids) => set({ bids, bidsLoading: false }),
  
  setBidsLoading: (loading) => set({ bidsLoading: loading }),
  
  // Quotes Actions
  setQuotes: (quotes) => set({ quotes, quotesLoading: false }),
  
  setQuotesLoading: (loading) => set({ quotesLoading: loading }),
  
  // Shortlisted Bids Actions
  setShortlistedBids: (bids) => set({ shortlistedBids: bids }),
  
  addShortlistedBid: (bid) => 
    set((state) => ({
      shortlistedBids: [...state.shortlistedBids, bid]
    })),
    
  removeShortlistedBid: (bidOptionId) =>
    set((state) => ({
      shortlistedBids: state.shortlistedBids.filter(bid => bid.bid_option_id !== bidOptionId)
    })),
    
  updateShortlistedBid: (bidOptionId, updates) =>
    set((state) => ({
      shortlistedBids: state.shortlistedBids.map(bid =>
        bid.bid_option_id === bidOptionId ? { ...bid, ...updates } : bid
      )
    })),
    
  clearShortlistedBids: () => set({ shortlistedBids: [] }),
  
  // Shortlisted Aircraft Actions
  addToShortlist: (aircraft) => 
    set((state) => {
      const exists = state.shortlistAircraft.some(a => a.aircraft_id === aircraft.aircraft_id);
      if (exists) return state;
      return { shortlistAircraft: [...state.shortlistAircraft, aircraft] };
    }),
    
  removeFromShortlist: (aircraftId) =>
    set((state) => ({
      shortlistAircraft: state.shortlistAircraft.filter(a => a.aircraft_id !== aircraftId)
    })),
    
  toggleShortlist: (aircraft) =>
    set((state) => {
      const exists = state.shortlistAircraft.some(a => a.aircraft_id === aircraft.aircraft_id);
      if (exists) {
        return { shortlistAircraft: state.shortlistAircraft.filter(a => a.aircraft_id !== aircraft.aircraft_id) };
      }
      return { shortlistAircraft: [...state.shortlistAircraft, aircraft] };
    }),

  updateShortlistAircraftPrice: (aircraftId, newPrice) =>
    set((state) => ({
      shortlistAircraft: state.shortlistAircraft.map(a =>
        (a.aircraft_id === aircraftId || a.bid_option_id === aircraftId)
          ? { ...a, myPrice: newPrice, total_price: newPrice }
          : a
      )
    })),

  clearShortlist: () => set({ shortlistAircraft: [] }),
  
  // Selected Bid Actions
  setSelectedBid: (bid) => set({ selectedBid: bid }),

  clearSelectedBid: () => set({ selectedBid: null }),

  // Selected Trip Group Actions
  setSelectedTripGroup: (tripNumber, bids) => set({ selectedTripGroup: { tripNumber, bids } }),

  clearSelectedTripGroup: () => set({ selectedTripGroup: null }),

  // Selected Aircraft Actions
  setSelectedAircraft: (aircraft) => set({ selectedAircraft: aircraft }),

  clearSelectedAircraft: () => set({ selectedAircraft: null }),

  // Page Header Override (for pages that need custom titles/subtitles in AppLayout header)
  pageHeaderOverride: null, // { title, subtitle, onEdit }
  setPageHeaderOverride: (override) => set({ pageHeaderOverride: override }),
  clearPageHeaderOverride: () => set({ pageHeaderOverride: null }),

  // Selected Bid Options Actions (for checkout)
  setSelectedBidOptions: (options) => set({ selectedBidOptions: options }),

  addSelectedBidOption: (option) =>
    set((state) => {
      const exists = state.selectedBidOptions.some(o => o.bid_option_id === option.bid_option_id);
      if (exists) return state;
      return { selectedBidOptions: [...state.selectedBidOptions, option] };
    }),

  removeSelectedBidOption: (bidOptionId) =>
    set((state) => ({
      selectedBidOptions: state.selectedBidOptions.filter(o => o.bid_option_id !== bidOptionId)
    })),

  clearSelectedBidOptions: () => set({ selectedBidOptions: [] }),
}));
